import { useState } from "react";
import { formatMoney } from "@shared/roi/format";
import type { YearResult } from "@shared/roi/types";
import {
  AxisChrome,
  ChartEmptyState,
  ChartFrame,
  ChartLegend,
  ChartTooltip,
  HoverSlot,
  YearTick,
  ZeroLine,
} from "./ChartChrome";
import {
  axisTicks,
  axisTop,
  bandRegions,
  CHART_W,
  endLabelY,
  PAD_L,
  PAD_R,
  PAD_T,
  PLOT_H,
  PLOT_W,
  tooltipLeftPct,
  type BandPoint,
} from "./geometry";

/**
 * Cumulative revenue against cumulative cost — where the lines cross is payback.
 *
 * The band between them is washed in the hue of whichever is ahead, split at the
 * interpolated crossing rather than at a year boundary, so "when does this pay
 * for itself" is answered by a shape rather than by reading two numbers.
 */
export function CumulativeChart({
  rows,
  breakEvenYears,
  currencyCode,
}: {
  rows: YearResult[];
  breakEvenYears: number | null;
  currencyCode: string;
}) {
  const [hover, setHover] = useState<number | null>(null);

  const last = rows[rows.length - 1];
  const max = rows.reduce((m, r) => Math.max(m, r.cumRevenue, r.cumCost), 0);
  const isEmpty = max <= 0 || !last;
  const ticks = axisTicks(max);
  const top = axisTop(ticks);
  const yFor = (value: number) => PAD_T + PLOT_H * (1 - value / top);
  const xFor = (yearPosition: number) => PAD_L + (yearPosition / Math.max(1, rows.length)) * PLOT_W;

  // Index 0 is the start of year 1, where both cumulative totals are zero.
  const points: BandPoint[] = [
    { x: xFor(0), yRevenue: yFor(0), yCost: yFor(0), diff: 0 },
    ...rows.map((row) => ({
      x: xFor(row.year),
      yRevenue: yFor(row.cumRevenue),
      yCost: yFor(row.cumCost),
      diff: row.cumRevenue - row.cumCost,
    })),
  ];

  const lineFor = (key: "yRevenue" | "yCost") =>
    points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p[key].toFixed(1)}`).join(" ");

  const regions = bandRegions(points);
  const end = points[points.length - 1];
  // Only direct-label the line ends when they are far enough apart to stay
  // attached to their own line; otherwise the legend and tooltip carry it.
  const labelEnds = end ? Math.abs(end.yRevenue - end.yCost) >= 16 : false;

  let breakEvenX: number | null = null;
  let breakEvenY: number | null = null;
  if (breakEvenYears !== null && !isEmpty) {
    const index = Math.min(points.length - 2, Math.max(0, Math.floor(breakEvenYears)));
    const a = points[index];
    const b = points[index + 1];
    if (a && b) {
      const t = Math.min(1, Math.max(0, breakEvenYears - index));
      breakEvenX = a.x + (b.x - a.x) * t;
      breakEvenY = a.yRevenue + (b.yRevenue - a.yRevenue) * t;
    }
  }

  const hovered = hover !== null ? rows[hover] : undefined;
  const hoverPoint = hover !== null ? points[hover + 1] : undefined;
  const money = (value: number, compact = false) => formatMoney(currencyCode, value, { compact });

  return (
    <ChartFrame
      label="Cumulative revenue against cumulative cost"
      overlay={
        <>
          {isEmpty && (
            <ChartEmptyState message="Add clients and prices to see when the product pays itself back." />
          )}
          {hovered && hoverPoint && !isEmpty && (
            <ChartTooltip
              leftPct={tooltipLeftPct(hoverPoint.x)}
              title={`Through year ${hovered.year}`}
              rows={[
                {
                  label: "cumulative revenue",
                  value: money(hovered.cumRevenue),
                  color: "var(--roi-revenue)",
                },
                {
                  label: "cumulative cost",
                  value: money(hovered.cumCost),
                  color: "var(--roi-cost)",
                },
                { label: "net position", value: money(hovered.cumNet) },
              ]}
            />
          )}
        </>
      }
      footer={
        <>
          <ChartLegend
            items={[
              { label: "Cumulative revenue", color: "var(--roi-revenue)", shape: "line" },
              { label: "Cumulative cost", color: "var(--roi-cost)", shape: "line" },
            ]}
          />
          {rows.length === 1 && !isEmpty && (
            <p className="mt-1 text-xs text-ink-soft">
              One year is a single step, not a curve. Raise the horizon to see the lines meet.
            </p>
          )}
        </>
      }
    >
      <AxisChrome ticks={ticks} top={top} currencyCode={currencyCode} />

      {!isEmpty &&
        regions.map((region, index) => (
          <path
            key={index}
            d={region.d}
            fill={region.positive ? "var(--roi-revenue)" : "var(--roi-cost)"}
            opacity={0.1}
          />
        ))}

      {!isEmpty && breakEvenX !== null && breakEvenY !== null && (
        <g>
          <line
            x1={breakEvenX}
            x2={breakEvenX}
            y1={PAD_T}
            y2={PAD_T + PLOT_H}
            stroke="var(--ink-soft)"
            strokeWidth={1}
            opacity={0.45}
          />
          <circle
            cx={breakEvenX}
            cy={breakEvenY}
            r={4.5}
            fill="var(--ink)"
            stroke="var(--surface-strong)"
            strokeWidth={2}
          />
          <text
            x={Math.min(breakEvenX + 8, CHART_W - PAD_R - 60)}
            y={PAD_T + 11}
            className="fill-[var(--ink-soft)] text-[11px] font-semibold"
          >
            Break-even
          </text>
        </g>
      )}

      {!isEmpty && end && last && (
        <>
          <path
            d={lineFor("yCost")}
            fill="none"
            stroke="var(--roi-cost)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={lineFor("yRevenue")}
            fill="none"
            stroke="var(--roi-revenue)"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle
            cx={end.x}
            cy={end.yCost}
            r={4}
            fill="var(--roi-cost)"
            stroke="var(--surface-strong)"
            strokeWidth={2}
          />
          <circle
            cx={end.x}
            cy={end.yRevenue}
            r={4}
            fill="var(--roi-revenue)"
            stroke="var(--surface-strong)"
            strokeWidth={2}
          />
          {labelEnds && (
            <>
              <text
                x={end.x - 6}
                y={endLabelY(end.yRevenue, end.yRevenue <= end.yCost)}
                textAnchor="end"
                className="tabular fill-[var(--ink-soft)] text-[11px] font-semibold"
              >
                {money(last.cumRevenue, true)}
              </text>
              <text
                x={end.x - 6}
                y={endLabelY(end.yCost, end.yCost < end.yRevenue)}
                textAnchor="end"
                className="tabular fill-[var(--ink-soft)] text-[11px] font-semibold"
              >
                {money(last.cumCost, true)}
              </text>
            </>
          )}
        </>
      )}

      {hoverPoint && !isEmpty && (
        <line
          x1={hoverPoint.x}
          x2={hoverPoint.x}
          y1={PAD_T}
          y2={PAD_T + PLOT_H}
          stroke="var(--ink-soft)"
          strokeWidth={1}
          opacity={0.3}
        />
      )}

      <ZeroLine y={yFor(0)} />

      {rows.map((row, index) => (
        <g key={row.year}>
          <YearTick x={xFor(row.year)} year={row.year} />
          <HoverSlot
            x={PAD_L + (index / Math.max(1, rows.length)) * PLOT_W}
            width={PLOT_W / Math.max(1, rows.length)}
            onEnter={() => setHover(index)}
            onLeave={() => setHover(null)}
          />
        </g>
      ))}
    </ChartFrame>
  );
}
